import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchQCObservations, getProjectsForCurrentUser } from "../../api/index";
import observationBanner from "../../Images/observation.jpg";
import {
  Plus,
  Search,
  ChevronDown,
  Hourglass,
  CheckCircle2,
  Settings,
  Lock,
  Eye,
  FileText,
  MapPin,
  MoreVertical,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  TriangleAlert,
} from "lucide-react";
import { SafetyHeroBanner } from "../Safety/SafetyBannerArt";

import { formatObservationDateTime } from "../../lib/utils";

const STATUS = {
  pending: {
    label: "Pending",
    fg: "#C1443A",
    bg: "#FBE2DF",
    icon: Hourglass,
  },
  in_progress: {
    label: "In Progress",
    fg: "#B4761F",
    bg: "#F8E9CD",
    icon: Settings,
  },
  resolved: {
    label: "Resolved",
    fg: "#3F8358",
    bg: "#DCEEE3",
    icon: CheckCircle2,
  },
  closed: {
    label: "Closed",
    fg: "#6B7280",
    bg: "#E9E7E2",
    icon: Lock,
  },
};

const inputCls =
  "w-full rounded-xl border pl-9 pr-9 py-2.5 text-[14px] bg-white outline-none transition-shadow focus:ring-2";

function StatusPill({ status }) {
  const tone = STATUS[status] || STATUS.pending;
  const Icon = tone.icon;

  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ color: tone.fg, background: tone.bg }}
    >
      <Icon size={13} strokeWidth={2.5} />
      {tone.label}
    </span>
  );
}

function Select({ value, onChange, options, placeholder, icon: LeadIcon }) {
  return (
    <div className="relative">
      {LeadIcon && (
        <LeadIcon
          size={15}
          className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ color: "#E07A1F" }}
        />
      )}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={`${inputCls} appearance-none cursor-pointer`}
        style={{
          borderColor: value ? "#E07A1F" : "#E4E0D8",
          color: value ? "#1B2430" : "#9A968C",
        }}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <ChevronDown
        size={16}
        className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
        style={{ color: "#6B7480" }}
      />
    </div>
  );
}

// Deterministic soft color for an assignee avatar, derived from their name.
const AVATAR_PALETTE = [
  { bg: "#DEE6EC", fg: "#3E5C76" },
  { bg: "#F8E9CD", fg: "#B4761F" },
  { bg: "#DCEEE3", fg: "#3F8358" },
  { bg: "#F5DCD9", fg: "#C1443A" },
  { bg: "#E7E1F2", fg: "#6C4DA0" },
];

function avatarTone(name) {
  const code = String(name || "?")
    .split("")
    .reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return AVATAR_PALETTE[code % AVATAR_PALETTE.length];
}

function initials(name) {
  const parts = String(name || "?").trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function AssigneeStack({ names }) {
  if (!names || names.length === 0) {
    return <span style={{ color: "#9A968C" }}>-</span>;
  }

  return (
    <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
      <div className="flex -space-x-1.5 shrink-0">
        {names.slice(0, 3).map((name, idx) => {
          const tone = avatarTone(name);
          return (
            <div
              key={`${name}-${idx}`}
              title={name}
              className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-[10px] sm:text-[10.5px] font-bold border-2 border-white shadow-xs"
              style={{ background: tone.bg, color: tone.fg }}
            >
              {initials(name)}
            </div>
          );
        })}
      </div>
      <span className="text-xs sm:text-[13px] text-slate-700 font-medium truncate max-w-[180px] sm:max-w-[240px]">
        {names.join(", ")}
      </span>
    </div>
  );
}

function normalizeObservationRows(observation, projectMap) {
  const items = Array.isArray(observation.items) ? observation.items : [];

  let pName = observation.project_name;
  if (!pName || /^project\s*#?\s*\d+$/i.test(String(pName).trim())) {
    if (projectMap && observation.project) {
      const matched = projectMap.get(String(observation.project));
      if (matched && !/^project\s*#?\s*\d+$/i.test(String(matched).trim())) {
        pName = matched;
      }
    }
  }

  // If there are multiple items in this observation, split each item into its own row so they appear as separate observations
  if (items.length > 1) {
    return items.map((item, index) => {
      const itemAssignees = Array.from(
        new Map(
          (item.assignments || [])
            .filter((assignment) => !assignment.is_skipped)
            .map((assignment) => [
              String(assignment.user_id),
              assignment.user_name || String(assignment.user_id),
            ]),
        ).values(),
      );

      return {
        ...observation,
        id: observation.id,
        rowKey: `${observation.id}-item-${item.id ?? index}`,
        displayId: `OBS-${String(observation.id).padStart(4, "0")} #${index + 1}`,
        project: observation.project,
        projectName: pName || "-",
        observationName: item.gap_type
          ? `${observation.observation_name || "Observation"} - ${item.gap_type}`
          : observation.observation_name || "-",
        categoryName: observation.category_name || "-",
        towerName: observation.tower_name || "-",
        floorName: observation.floor_name || observation.custom_location || "-",
        itemCount: 1,
        overallStatus: item.status || observation.overall_status || "pending",
        assigneeNames:
          itemAssignees.length > 0
            ? itemAssignees
            : observation.created_by_name
            ? [observation.created_by_name]
            : [],
        gapTypes: item.gap_type ? [item.gap_type] : [],
        createdDate: formatObservationDateTime(observation.created_at),
        itemId: item.id,
      };
    });
  }

  const activeAssignees = Array.from(
    new Map(
      items
        .flatMap((item) => item.assignments || [])
        .filter((assignment) => !assignment.is_skipped)
        .map((assignment) => [
          String(assignment.user_id),
          assignment.user_name || String(assignment.user_id),
        ]),
    ).values(),
  );

  const gapTypes = Array.from(
    new Set(items.map((item) => item.gap_type).filter(Boolean)),
  );

  return [
    {
      ...observation,
      id: observation.id,
      rowKey: String(observation.id),
      displayId: `OBS-${String(observation.id).padStart(4, "0")}`,
      project: observation.project,
      projectName: pName || "-",
      observationName: observation.observation_name || "-",
      categoryName: observation.category_name || "-",
      towerName: observation.tower_name || "-",
      floorName: observation.floor_name || observation.custom_location || "-",
      itemCount: 1,
      overallStatus: observation.overall_status || items[0]?.status || "pending",
      assigneeNames: activeAssignees,
      gapTypes,
      createdDate: formatObservationDateTime(observation.created_at),
      itemId: items[0]?.id,
    },
  ];
}

function decodeJWT(token) {
  try {
    if (!token || typeof token !== "string" || !token.includes(".")) return null;
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

export default function ObservationDashboard({ currentUser: propUser }) {
  const navigate = useNavigate();

  // Dynamically resolve the logged-in user profile, role, and explicit project assignments
  const userProfile = useMemo(() => {
    let tokenData = null;
    try {
      const token =
        localStorage.getItem("ACCESS_TOKEN") ||
        localStorage.getItem("token") ||
        localStorage.getItem("access") ||
        localStorage.getItem("TOKEN");
      if (token) tokenData = decodeJWT(token);
    } catch {}

    let userData = null;
    try {
      const rawUser = localStorage.getItem("USER_DATA");
      if (rawUser && rawUser !== "undefined") {
        userData = JSON.parse(rawUser);
      }
    } catch {}

    let storedAccesses = [];
    try {
      const rawAcc = localStorage.getItem("ACCESSES");
      if (rawAcc && rawAcc !== "undefined") {
        const parsed = JSON.parse(rawAcc);
        if (Array.isArray(parsed)) storedAccesses = parsed;
      }
    } catch {}

    const roleName = (
      propUser?.role_name ||
      (tokenData?.roles && tokenData.roles[0]) ||
      (userData?.roles && userData.roles[0]) ||
      localStorage.getItem("ROLE") ||
      ""
    ).toLowerCase();

    const isSuperAdmin = Boolean(
      tokenData?.superadmin ||
        userData?.superadmin ||
        roleName.includes("super admin"),
    );

    // Extract all assigned projects from JWT accesses, localStorage ACCESSES, userData, and PM/PH projects
    const assignedMap = new Map();
    const addAccess = (acc) => {
      if (!acc) return;
      if (acc.active === false) return;
      const pid = acc.project_id ?? acc.id ?? acc.project;
      const pname = acc.project_name ?? acc.name ?? acc.title;
      if (pid != null && !assignedMap.has(Number(pid))) {
        let cleanPname = pname ? String(pname).trim() : "";
        if (!cleanPname || /^project\s*#?\s*\d+$/i.test(cleanPname)) {
          const storedActivePid =
            localStorage.getItem("ACTIVE_PROJECT_ID") ||
            localStorage.getItem("PROJECT_ID") ||
            localStorage.getItem("project_id");
          const storedActivePname =
            localStorage.getItem("ACTIVE_PROJECT_NAME") ||
            localStorage.getItem("PROJECT_NAME") ||
            localStorage.getItem("project_name");
          if (
            storedActivePid &&
            String(storedActivePid) === String(pid) &&
            storedActivePname &&
            !/^project\s*#?\s*\d+$/i.test(storedActivePname.trim())
          ) {
            cleanPname = storedActivePname.trim();
          } else {
            try {
              const cached = JSON.parse(
                localStorage.getItem("projects") ||
                  localStorage.getItem("PROJECTS") ||
                  localStorage.getItem("PROJECT_LIST") ||
                  "[]",
              );
              if (Array.isArray(cached)) {
                const match = cached.find(
                  (cp) => String(cp?.id || cp?.project_id) === String(pid),
                );
                const cName = match?.name || match?.project_name || match?.title;
                if (cName && !/^project\s*#?\s*\d+$/i.test(String(cName).trim())) {
                  cleanPname = String(cName).trim();
                }
              }
            } catch {}
          }
        }

        assignedMap.set(Number(pid), {
          id: Number(pid),
          name: cleanPname || `Project ${pid}`,
        });
      }
    };

    if (Array.isArray(tokenData?.accesses)) tokenData.accesses.forEach(addAccess);
    storedAccesses.forEach(addAccess);
    if (Array.isArray(userData?.accesses)) userData.accesses.forEach(addAccess);
    if (Array.isArray(tokenData?.project_manager_projects))
      tokenData.project_manager_projects.forEach(addAccess);
    if (Array.isArray(tokenData?.project_head_projects))
      tokenData.project_head_projects.forEach(addAccess);

    const assignedProjects = Array.from(assignedMap.values());

    return {
      userId:
        propUser?.user_id ??
        tokenData?.user_id ??
        userData?.user_id ??
        userData?.id,
      username: propUser?.username ?? tokenData?.username ?? userData?.username,
      roleName,
      isSuperAdmin,
      hasAssignedProjects: assignedProjects.length > 0,
      assignedProjects,
    };
  }, [propUser]);

  const currentUser = useMemo(
    () => ({
      user_id: userProfile.userId,
      username: userProfile.username,
      role_name: userProfile.roleName,
    }),
    [userProfile],
  );

  const [userProjects, setUserProjects] = useState([]);
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState(() => {
    const activePid =
      localStorage.getItem("ACTIVE_PROJECT_ID") ||
      localStorage.getItem("PROJECT_ID") ||
      "";
    return {
      project: activePid,
      status: "",
      tower: "",
    };
  });

  // Fetch / resolve user projects dynamically
  useEffect(() => {
    // 1) Show assigned projects immediately if available
    if (userProfile.hasAssignedProjects) {
      setUserProjects(userProfile.assignedProjects);
    }

    // 2) Enrich synthetic names or fetch full project list for super admins via getProjectsForCurrentUser
    getProjectsForCurrentUser()
      .then((res) => {
        const raw = res?.data || [];
        const normalized = Array.isArray(raw)
          ? raw.map((p) => ({
              id: p.id ?? p.project_id ?? p.project,
              name:
                p.name ??
                p.project_name ??
                p.title ??
                (p.project ? `Project ${p.project}` : `Project ${p.id}`),
            }))
          : [];
        const validProjects = normalized.filter((p) => p.id);
        if (validProjects.length > 0) {
          if (!userProfile.isSuperAdmin && userProfile.hasAssignedProjects) {
            const nameMap = new Map(
              validProjects.map((p) => [String(p.id), p.name]),
            );
            setUserProjects((prev) =>
              prev.map((proj) => {
                const realName = nameMap.get(String(proj.id));
                return {
                  ...proj,
                  name:
                    realName && !/^project\s*#?\s*\d+$/i.test(realName.trim())
                      ? realName
                      : proj.name,
                };
              }),
            );
          } else {
            setUserProjects(validProjects);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to fetch user projects:", err);
      });
  }, [userProfile]);

  // Ensure activePid from localStorage doesn't point to an unassigned project
  useEffect(() => {
    if (!userProfile.isSuperAdmin && userProjects.length > 0 && filters.project) {
      const isAssigned = userProjects.some(
        (p) => String(p.id || p.project_id) === String(filters.project),
      );
      if (!isAssigned) {
        setFilters((prev) => ({ ...prev, project: "" }));
      }
    }
  }, [userProjects, userProfile.isSuperAdmin, filters.project]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (userProfile.roleName === "maker") {
        params.assigned_user_id = userProfile.userId;
      }

      // If user selected a specific project from dropdown:
      if (filters.project) {
        params.project = filters.project;
      } else if (!userProfile.isSuperAdmin && userProjects.length > 0) {
        // If "All Projects" is selected, only query the user's assigned projects!
        const projectIds = userProjects
          .map((p) => p.id || p.project_id)
          .filter(Boolean);
        if (projectIds.length > 0) {
          params.project = projectIds.join(",");
        }
      } else if (
        !userProfile.isSuperAdmin &&
        userProfile.hasAssignedProjects &&
        userProjects.length === 0
      ) {
        const projectIds = userProfile.assignedProjects
          .map((p) => p.id)
          .filter(Boolean);
        if (projectIds.length > 0) {
          params.project = projectIds.join(",");
        }
      }

      // If user has 0 assigned projects and is not super admin, show empty
      if (
        !userProfile.isSuperAdmin &&
        !userProfile.hasAssignedProjects &&
        userProjects.length === 0
      ) {
        setObservations([]);
        setLoading(false);
        return;
      }

      const response = await fetchQCObservations(params);
      setObservations(
        Array.isArray(response.data)
          ? response.data
          : response.data?.results || [],
      );
    } catch (error) {
      console.error("Failed to fetch observations", error);
    } finally {
      setLoading(false);
    }
  }, [
    userProfile.roleName,
    userProfile.userId,
    userProfile.isSuperAdmin,
    userProfile.hasAssignedProjects,
    userProfile.assignedProjects,
    filters.project,
    userProjects,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Set of assigned project IDs for client-side filtering guard
  const assignedIdSet = useMemo(() => {
    if (userProfile.isSuperAdmin) return null;
    const ids = userProjects
      .map((p) => Number(p.id || p.project_id))
      .filter(Boolean);
    if (ids.length === 0 && userProfile.hasAssignedProjects) {
      return new Set(userProfile.assignedProjects.map((p) => Number(p.id)));
    }
    return ids.length > 0 ? new Set(ids) : null;
  }, [
    userProfile.isSuperAdmin,
    userProjects,
    userProfile.hasAssignedProjects,
    userProfile.assignedProjects,
  ]);

  const projectMap = useMemo(() => {
    const map = new Map();
    userProjects.forEach((p) => {
      const pid = p.id || p.project_id;
      const pname = p.name || p.project_name || p.title;
      if (pid && pname) {
        map.set(String(pid), String(pname).trim());
      }
    });
    return map;
  }, [userProjects]);

  const rows = useMemo(() => {
    let list = observations.flatMap((obs) => normalizeObservationRows(obs, projectMap));
    // Security & isolation guard: never show observations from unassigned projects
    if (assignedIdSet && assignedIdSet.size > 0) {
      list = list.filter((row) => assignedIdSet.has(Number(row.project)));
    }
    return list;
  }, [observations, assignedIdSet, projectMap]);

  // Project dropdown options: strictly derived from user's assigned projects (or all for super admin)
  const projectOptions = useMemo(() => {
    return userProjects.map((p) => ({
      value: String(p.id || p.project_id),
      label: p.name || p.project_name || p.title || `Project ${p.id}`,
    }));
  }, [userProjects]);

  const towerOptions = useMemo(() => {
    const values = Array.from(
      new Set(
        rows
          .map((row) => row.towerName)
          .filter((value) => value && value !== "-"),
      ),
    );

    return values.map((value) => ({ value, label: value }));
  }, [rows]);

  const counts = useMemo(() => {
    return rows.reduce(
      (result, item) => {
        result.total += 1;
        result[item.overallStatus] = (result[item.overallStatus] || 0) + 1;
        return result;
      },
      {
        total: 0,
        pending: 0,
        in_progress: 0,
        resolved: 0,
        closed: 0,
      },
    );
  }, [rows]);

  const filteredRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return rows.filter((row) => {
      if (
        filters.project &&
        String(row.project) !== String(filters.project)
      ) {
        return false;
      }

      if (filters.status && row.overallStatus !== filters.status) {
        return false;
      }

      if (filters.tower && row.towerName !== filters.tower) {
        return false;
      }

      if (normalizedQuery) {
        const searchableText = [
          row.displayId,
          row.observationName,
          row.projectName,
          row.categoryName,
          (row.gapTypes || []).join(" "),
          row.towerName,
          row.floorName,
          row.assigneeNames.join(" "),
        ]
          .join(" ")
          .toLowerCase();

        if (!searchableText.includes(normalizedQuery)) {
          return false;
        }
      }

      return true;
    });
  }, [rows, query, filters]);

  // --- Presentational-only pagination (does not touch fetching/filtering logic above) ---
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    setPage(1);
  }, [query, filters, rows.length]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageStart = filteredRows.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const pageEnd = Math.min(safePage * pageSize, filteredRows.length);
  const pagedRows = useMemo(
    () => filteredRows.slice((safePage - 1) * pageSize, safePage * pageSize),
    [filteredRows, safePage, pageSize],
  );

  const statMeta = [
    { key: "total", label: "Total", icon: FileText, fg: "#1B2430", bg: "#EFEDE7", accent: "#E07A1F" },
    { key: "pending", label: "Pending", icon: Hourglass, fg: "#C1443A", bg: "#FBE2DF", accent: "#C1443A" },
    { key: "in_progress", label: "In Progress", icon: Settings, fg: "#B4761F", bg: "#F8E9CD", accent: "#B4761F" },
    { key: "resolved", label: "Resolved", icon: CheckCircle2, fg: "#3F8358", bg: "#DCEEE3", accent: "#3F8358" },
    { key: "closed", label: "Closed", icon: Lock, fg: "#6B7280", bg: "#E9E7E2", accent: "#6B7280" },
  ];

  const statSub = {
    total: "All Observations",
    pending: "Awaiting Action",
    in_progress: "Under Review",
    resolved: "Successfully Resolved",
    closed: "Closed Observations",
  };

  const statCard = (meta) => {
    const Icon = meta.icon;
    const isClosed = meta.key === "closed";
    const isActive =
      (meta.key === "total" && !filters.status) || filters.status === meta.key;

    return (
      <div
        key={meta.key}
        onClick={() => {
          if (meta.key === "total") {
            setFilters((f) => ({ ...f, status: "" }));
          } else {
            setFilters((f) => ({
              ...f,
              status: f.status === meta.key ? "" : meta.key,
            }));
          }
          setPage(1);
        }}
        className={`relative cursor-pointer transition-all duration-150 w-full min-w-0 p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl border overflow-hidden shadow-xs hover:shadow-md ${
          isClosed ? "col-span-2 sm:col-span-1" : ""
        } ${isActive ? "ring-2 ring-orange-500" : ""}`}
        style={{ borderColor: isActive ? meta.accent : "#EAE6DD" }}
      >
        {isClosed ? (
          <>
            {/* Mobile horizontal layout for closed card so it fills the 2-column span neatly */}
            <div className="flex sm:hidden items-center justify-between">
              <div className="flex items-center gap-2.5 min-w-0">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: meta.bg }}
                >
                  <Icon size={16} strokeWidth={2.25} style={{ color: meta.fg }} />
                </div>
                <div className="min-w-0">
                  <div
                    className="text-[11px] font-semibold uppercase tracking-wide truncate"
                    style={{ color: "#9A968C" }}
                  >
                    {meta.label}
                  </div>
                  <div className="text-[11px] truncate" style={{ color: "#9A968C" }}>
                    {statSub[meta.key]}
                  </div>
                </div>
              </div>
              <div className="text-2xl font-bold shrink-0 ml-2" style={{ color: "#1B2430" }}>
                {counts[meta.key]}
              </div>
            </div>

            {/* Tablet & Desktop layout */}
            <div className="hidden sm:block">
              <div className="flex items-center gap-2.5 sm:gap-3">
                <div
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: meta.bg }}
                >
                  <Icon size={17} strokeWidth={2.25} style={{ color: meta.fg }} />
                </div>
                <div
                  className="text-[11px] lg:text-[12px] font-semibold uppercase tracking-wide truncate"
                  style={{ color: "#9A968C" }}
                >
                  {meta.label}
                </div>
              </div>
              <div
                className="text-2xl lg:text-3xl font-bold mt-2 sm:mt-2.5"
                style={{ color: "#1B2430" }}
              >
                {counts[meta.key]}
              </div>
              <div
                className="text-[11px] lg:text-[12px] mt-0.5 truncate"
                style={{ color: "#9A968C" }}
              >
                {statSub[meta.key]}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 sm:gap-3">
              <div
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: meta.bg }}
              >
                <Icon size={16} strokeWidth={2.25} style={{ color: meta.fg }} />
              </div>
              <div
                className="text-[11px] sm:text-[12px] font-semibold uppercase tracking-wide truncate"
                style={{ color: "#9A968C" }}
              >
                {meta.label}
              </div>
            </div>
            <div
              className="text-2xl sm:text-3xl font-bold mt-2 sm:mt-2.5"
              style={{ color: "#1B2430" }}
            >
              {counts[meta.key]}
            </div>
            <div
              className="text-[11px] sm:text-[12px] mt-0.5 truncate"
              style={{ color: "#9A968C" }}
            >
              {statSub[meta.key]}
            </div>
          </>
        )}

        <div
          className="absolute bottom-0 left-0 right-0 h-[3px]"
          style={{ background: meta.accent }}
        />
      </div>
    );
  };

  return (
    <div
      className="min-h-screen w-full max-w-full overflow-x-hidden p-3 sm:p-6 md:p-8 font-sans"
      style={{ background: "#F6F5F1" }}
    >
      <style>{`
        input:focus,
        select:focus {
          box-shadow: 0 0 0 3px rgba(224, 122, 31, 0.18);
          border-color: #E07A1F;
        }
      `}</style>

      <div className="max-w-[1200px] mx-auto w-full min-w-0">
        <div className="mb-5 sm:mb-7">
          <SafetyHeroBanner
            type="observation"
            categoryTag="SITE QUALITY CONTROL"
            categoryIcon={TriangleAlert}
            title="Observation Dashboard"
            subtitle="Track, manage, and resolve site quality observations efficiently."
          >
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={loadData}
                disabled={loading}
                className="flex items-center gap-1.5 sm:gap-2 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl font-semibold text-xs sm:text-sm border border-slate-200 bg-white/90 backdrop-blur-xs disabled:opacity-60 shadow-xs hover:bg-slate-50 transition-colors text-slate-700 cursor-pointer"
              >
                <RefreshCw size={14} className={loading ? "animate-spin text-orange-500" : ""} />
                Refresh
              </button>

              {currentUser.role_name === "checker" && (
                <button
                  type="button"
                  onClick={() => navigate("/qc/observations/create")}
                  className="flex items-center gap-1.5 sm:gap-2 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl font-semibold text-xs sm:text-sm text-white shadow-sm hover:opacity-95 transition-all whitespace-nowrap cursor-pointer"
                  style={{
                    background: "linear-gradient(135deg, #EF8C2B, #E0691A)",
                  }}
                >
                  <Plus size={16} />
                  Raise Observation
                </button>
              )}
            </div>
          </SafetyHeroBanner>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-4 mb-5 sm:mb-7">
          {statMeta.map(statCard)}
        </div>

        <div
          className="bg-white rounded-2xl border p-3.5 sm:p-5 mb-5 sm:mb-7 shadow-xs"
          style={{ borderColor: "#EAE6DD" }}
        >
          <div className="relative mb-3 sm:mb-4">
            <Search
              size={16}
              className="absolute left-3.5 top-1/2 -translate-y-1/2"
              style={{ color: "#9A968C" }}
            />
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
              placeholder="Search by ID, project, category, gap type, or assignee..."
              className="w-full min-w-0 outline-none text-xs sm:text-sm bg-[#FAF9F5] rounded-xl pl-10 pr-4 py-2.5 border transition-all"
              style={{ borderColor: "#EAE6DD" }}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
            <Select
              value={filters.project}
              onChange={(value) => {
                setFilters((current) => ({
                  ...current,
                  project: value,
                }));
                setPage(1);
              }}
              options={projectOptions}
              placeholder="All Projects"
              icon={FileText}
            />

            <Select
              value={filters.tower}
              onChange={(value) => {
                setFilters((current) => ({
                  ...current,
                  tower: value,
                }));
                setPage(1);
              }}
              options={towerOptions}
              placeholder="All Towers"
              icon={MapPin}
            />

            <Select
              value={filters.status}
              onChange={(value) => {
                setFilters((current) => ({
                  ...current,
                  status: value,
                }));
                setPage(1);
              }}
              options={Object.entries(STATUS).map(([value, config]) => ({
                value,
                label: config.label,
              }))}
              placeholder="All Statuses"
              icon={ChevronDown}
            />
          </div>
        </div>

        <div
          className="bg-white rounded-2xl border overflow-hidden shadow-sm"
          style={{ borderColor: "#EAE6DD" }}
        >
          {/* Header strip: All Records & count */}
          <div className="px-4 sm:px-6 py-3.5 border-b border-slate-200 bg-slate-50/70 flex items-center justify-between gap-2 min-w-0">
            <h2 className="text-xs sm:text-sm font-bold text-slate-900 flex items-center gap-2 truncate">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" />
              <span className="truncate">
                {filters.status ? STATUS[filters.status]?.label || "All Records" : "All Records"}
              </span>
            </h2>
            <div className="text-xs text-slate-500 font-medium shrink-0">
              {filteredRows.length} {filteredRows.length === 1 ? "entry" : "entries"} found
            </div>
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr
                  className="text-left"
                  style={{
                    background: "#F9F8F4",
                    color: "#8A8F79",
                  }}
                >
                  <th className="px-5 py-3 font-semibold">ID</th>
                  <th className="px-5 py-3 font-semibold">Project</th>
                  <th className="px-5 py-3 font-semibold">Location</th>
                  <th className="px-5 py-3 font-semibold">Items</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Assignee</th>
                  <th className="px-5 py-3 font-semibold">Created</th>
                  <th className="px-5 py-3 font-semibold text-right">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-5 py-10 text-center"
                      style={{ color: "#9A968C" }}
                    >
                      Loading observations...
                    </td>
                  </tr>
                )}

                {!loading &&
                  pagedRows.map((row) => (
                    <tr
                      key={row.rowKey || row.id}
                      className="transition-colors hover:bg-[#FBF8F3]"
                      style={{ borderTop: "1px solid #EFEDE7" }}
                    >
                      <td
                        className="px-5 py-3 font-bold"
                        style={{ color: "#1B2430" }}
                      >
                        {row.displayId}
                      </td>

                      <td className="px-5 py-3">
                        <span
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[12.5px] font-semibold"
                          style={{ color: "#B4610F", background: "#FBEADA" }}
                        >
                          <FileText size={12} />
                          {row.projectName}
                        </span>
                      </td>

                      <td className="px-5 py-3" style={{ color: "#3A4552" }}>
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin size={13} style={{ color: "#9A968C" }} />
                          {row.towerName} · {row.floorName}
                        </span>
                      </td>

                      <td className="px-5 py-3" style={{ color: "#3A4552" }}>
                        {row.itemCount}
                      </td>

                      <td className="px-5 py-3">
                        <StatusPill status={row.overallStatus} />
                      </td>

                      <td className="px-5 py-3">
                        <AssigneeStack names={row.assigneeNames} />
                      </td>

                      <td
                        className="px-5 py-3 text-[12.5px]"
                        style={{ color: "#8A8F79" }}
                      >
                        {row.createdDate}
                      </td>

                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                `/qc/observations/${row.id}${
                                  row.itemId ? `?item=${row.itemId}` : ""
                                }`,
                              )
                            }
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-semibold"
                            style={{
                              background:
                                currentUser.role_name === "checker" &&
                                row.overallStatus === "resolved"
                                  ? "#FBEADA"
                                  : "#DEE6EC",
                              color:
                                currentUser.role_name === "checker" &&
                                row.overallStatus === "resolved"
                                  ? "#B4610F"
                                  : "#3E5C76",
                            }}
                          >
                            <Eye size={14} />
                            {currentUser.role_name === "checker" &&
                            row.overallStatus === "resolved"
                              ? "Review"
                              : "View"}
                          </button>

                          <button
                            type="button"
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ color: "#9A968C" }}
                            aria-label="More actions"
                          >
                            <MoreVertical size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                {!loading && filteredRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-5 py-10 text-center"
                      style={{ color: "#9A968C" }}
                    >
                      No observations match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Stacked Card View (Responsive block cards mirroring NCR Dashboard) */}
          <div className="sm:hidden flex flex-col divide-y divide-slate-100">
            {loading && (
              <div className="py-12 flex flex-col items-center justify-center text-slate-400 space-y-3">
                <RefreshCw className="w-7 h-7 animate-spin text-orange-500" />
                <p className="text-sm">Loading observations...</p>
              </div>
            )}

            {!loading && filteredRows.length === 0 && (
              <div className="py-12 flex flex-col items-center justify-center text-slate-400 space-y-2 text-center px-4">
                <p className="text-sm font-medium text-slate-600">No observations match the current filters.</p>
                <p className="text-xs text-slate-400">Try clearing filters or search to see more results.</p>
              </div>
            )}

            {!loading &&
              pagedRows.map((row) => {
                const borderClass =
                  row.overallStatus === "pending"
                    ? "border-l-[#C1443A]"
                    : row.overallStatus === "in_progress"
                    ? "border-l-[#B4761F]"
                    : row.overallStatus === "resolved"
                    ? "border-l-[#3F8358]"
                    : "border-l-[#6B7280]";

                return (
                  <div
                    key={row.rowKey || row.id}
                    onClick={() =>
                      navigate(
                        `/qc/observations/${row.id}${
                          row.itemId ? `?item=${row.itemId}` : ""
                        }`,
                      )
                    }
                    className={`p-3.5 sm:p-4 cursor-pointer hover:bg-orange-50/40 bg-white border-l-4 ${borderClass} transition-colors duration-150 min-w-0 w-full overflow-hidden`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-2 min-w-0">
                      <span className="font-mono font-bold text-slate-900 text-sm sm:text-base tracking-tight truncate">
                        {row.displayId}
                      </span>
                      <div
                        className="shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/qc/observations/${row.id}${
                              row.itemId ? `?item=${row.itemId}` : ""
                            }`,
                          );
                        }}
                      >
                        <StatusPill status={row.overallStatus} />
                      </div>
                    </div>

                    <div className="text-xs sm:text-sm text-slate-700 font-medium mb-1.5 flex items-center gap-1.5 flex-wrap min-w-0">
                      <span className="font-semibold text-slate-900">{row.projectName}</span>
                      <span className="text-slate-400">/</span>
                      <span className="text-slate-600">{row.towerName} · {row.floorName}</span>
                    </div>

                    <div className="text-xs text-slate-600 mb-2 flex items-start gap-1.5 min-w-0">
                      <span className="text-slate-400 font-medium shrink-0 pt-0.5">Assignee:</span>
                      <div className="min-w-0 flex-1">
                        <AssigneeStack names={row.assigneeNames} />
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-2 text-xs text-slate-500 min-w-0 flex-wrap">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-orange-50 text-orange-700 border border-orange-200 shrink-0">
                        {row.itemCount} {row.itemCount === 1 ? "Item" : "Items"}
                      </span>
                      <span className="text-[11px] sm:text-xs text-slate-500 truncate">
                        Created: {row.createdDate}
                      </span>
                    </div>

                    <div
                      className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-end"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            `/qc/observations/${row.id}${
                              row.itemId ? `?item=${row.itemId}` : ""
                            }`,
                          )
                        }
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200 shadow-xs transition-colors active:scale-95"
                      >
                        <Eye size={13} />
                        {currentUser.role_name === "checker" && row.overallStatus === "resolved"
                          ? "Review"
                          : "View Report"}
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>

          {!loading && filteredRows.length > 0 && (
            <div
              className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-6 py-3.5"
              style={{ borderTop: "1px solid #EFEDE7" }}
            >
              <div className="text-xs sm:text-[13px] text-slate-500 text-center sm:text-left">
                Showing {pageStart} to {pageEnd} of {filteredRows.length} results
              </div>

              <div className="flex items-center gap-1.5 sm:gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="w-8 h-8 rounded-lg border flex items-center justify-center disabled:opacity-40 transition-colors hover:bg-slate-50"
                  style={{ borderColor: "#DEDAD1", color: "#3A4552" }}
                >
                  <ChevronLeft size={15} />
                </button>

                {Array.from({ length: pageCount }).slice(0, 5).map((_, idx) => {
                  const n = idx + 1;
                  const active = n === safePage;
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setPage(n)}
                      className="w-8 h-8 rounded-lg border text-xs sm:text-[13px] font-semibold transition-colors"
                      style={{
                        borderColor: active ? "#E07A1F" : "#DEDAD1",
                        color: active ? "#E07A1F" : "#3A4552",
                        background: active ? "#FCEBDC" : "#FFFFFF",
                      }}
                    >
                      {n}
                    </button>
                  );
                })}

                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  disabled={safePage === pageCount}
                  className="w-8 h-8 rounded-lg border flex items-center justify-center disabled:opacity-40 transition-colors hover:bg-slate-50"
                  style={{ borderColor: "#DEDAD1", color: "#3A4552" }}
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}